import { FormInstance, Row } from "antd";
import { useMemo, useState, useCallback } from "react";
import { FloatNumberInput } from "../../../../../../../../../../components/FloatNumberInput";
import { PayeeInput } from "../PayeeInput";
import { TypeSelect } from "./TypeSelect";
import { AmountSwitcher } from "./AmountSwitcher";
import { useSelectValue } from "../../../../../../../../../../lib/hooks/useSelectValue";
import { transformToFloat } from "../../../../../../../../../../lib/transformToFloat";
import { TPayeeStatusState } from "../../types";
import { usePercentageRule } from "../../../../../../../../../../lib/hooks/usePercentageRule";

type TPayeeFormProps = {
  form: FormInstance;
  payeesNumber: {
    primaryBuyerNumber: number;
    primarySellerNumber: number;
  };
  isFormDisabled: boolean;
  isHistoryOpened?: boolean;
  isPayment?: boolean;
  isPassthru?: boolean;
  payeeStatusState?: TPayeeStatusState;
  setPayeeStatusState?: React.Dispatch<React.SetStateAction<TPayeeStatusState>>;
};

export const MainPart = ({
  form,
  payeesNumber,
  isFormDisabled,
  isHistoryOpened = false,
  isPayment,
  isPassthru,
  payeeStatusState,
  setPayeeStatusState
}: TPayeeFormProps) => {
  const [isPercentage, setIsPercentage] = useState<boolean>(form.getFieldValue("amountSwitcher") === 2);

  const type: number = form.getFieldValue("type");

  const initRadioVal = (isPassthru ? [1, 2, 5, 6] : [1, 2]).includes(type);

  const [isRadioVisible, setIsRadioVisible] = useState<boolean>(initRadioVal);

  const { percentageRules } = usePercentageRule();

  const isAmountDisabled: boolean = useMemo(() => {
    if (isFormDisabled) {
      return true;
    }

    if (payeeStatusState) {
      const { isActive, isAmountNonNullOrZero, isEnableForPayments } = payeeStatusState;

      return !isActive && !isAmountNonNullOrZero && !isEnableForPayments;
    }

    return false;
  }, [payeeStatusState, isFormDisabled]);

  const { ref } = useSelectValue(!!isPayment);

  const onAmountChange = useCallback(() => {
    if (setPayeeStatusState) {
      const formattedValue = form.getFieldValue(isPercentage ? "percentage" : "amount");

      const value = transformToFloat(formattedValue);

      setPayeeStatusState((prev: any) => ({
        ...prev,
        isAmountNonNullOrZero: value !== null && value > 0
      }));
    }
  }, [form, setPayeeStatusState, isPercentage]);

  return (
    <div className="pb-4">
      <TypeSelect
        form={form}
        isHistoryOpened={isHistoryOpened}
        setIsRadioVisible={setIsRadioVisible}
        setIsPercentage={setIsPercentage}
        isPassthru={isPassthru}
        isPayment={isPayment}
        isFormDisabled={isFormDisabled}
      />
      <PayeeInput
        form={form}
        label="Payee"
        name="payeeNumber"
        idItemName="payeeId"
        payeesNumber={payeesNumber}
        isFormDisabled={isFormDisabled}
      />
      {!isHistoryOpened && (
        <PayeeInput
          form={form}
          label="Alternate Payee"
          name="alternativePayeeNumber"
          idItemName="alternativePayeeId"
          payeesNumber={payeesNumber}
          isFormDisabled={isFormDisabled}
          isFieldRequired={false}
        />
      )}
      <Row>
        {isPercentage ? (
          <FloatNumberInput
            name="percentage"
            label="Amount"
            prefix="%"
            decimalLimit={4}
            form={form}
            className="flex-grow-1"
            rules={percentageRules}
            disabled={isAmountDisabled}
            onChange={onAmountChange}
          />
        ) : (
          <FloatNumberInput
            name="amount"
            label="Amount"
            form={form}
            className="flex-grow-1"
            rules={[{ required: true }]}
            customRef={ref}
            disabled={isAmountDisabled}
            onChange={onAmountChange}
            includeThousandsSeparator
          />
        )}
        {!isPayment && isRadioVisible && <AmountSwitcher setIsPercentage={setIsPercentage} />}
      </Row>
    </div>
  );
};
